import React, { useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import { PairDeviceProps } from './interfaces';
import { ScreenEnum } from '../../enums';
import Progress from '../../components/Progress';
import { useTranslation } from 'react-i18next';
import AppStateContext from '../../AppStateProvider/context';
import {
	ExchangeDeviceCodeResponse,
	GetDeviceCodeResponse,
	requestDeviceCode,
} from '../../utils/pairing';
import {
	createPollingLoop,
	fetchDeliveryAccessToken,
	getBackoffDelay,
	refreshAccessToken,
} from '../../utils/pairingHelpers';
import { useTimeout } from '../../utils/useTimeout';
import { debugLogResponse } from '../../utils/debugLogResponse';

export default function PairDevice(props: PairDeviceProps) {
	const { t } = useTranslation();
	const { state, dispatch } = useContext(AppStateContext);
	const [deviceData, setDeviceData] = useState<GetDeviceCodeResponse>();
	const [countdown, setCountdown] = useState<number>(900);
	const [statusMsg, setStatusMsg] = useState<string>('');
	const [errorMsg, setErrorMsg] = useState<string>('');
	const [isDelivering, setIsDelivering] = useState<boolean>(false);
	const [deliveryAccessToken, setDeliveryAccessToken] = useState<
		string | null
	>(null);

	const useQs = state.expertMode;
	const qsBase = state.market?.qsUrl || '';

	const pairingBase =
		(useQs && qsBase ? qsBase : state.market?.urls?.pairing || '' ) +
		'/module/devices/bpca';

	const deliveryBase =
		(useQs && qsBase ? qsBase : state.market?.urls?.delivery || '' ) +
		'/module/delivery/bpca';

	const stopRef = useRef(false);
	const tokenPollTimer = useTimeout();
	const deliveryTimer = useTimeout();
	const navTimer = useTimeout();
	const deliveryStartedRef = useRef<boolean>(false);

	const handleCancel = () => {
		stopRef.current = true;
		tokenPollTimer.clear();
		deliveryTimer.clear();
		navTimer.clear();

		// Reset local state
		setIsDelivering(false);
		setStatusMsg('');
		setErrorMsg('');
		setDeviceData(undefined);
		setDeliveryAccessToken(null);
		setCountdown(900);
		deliveryStartedRef.current = false;

		// Reset AppState context
		dispatch({ type: 'SET_PAIRING', value: false });
		dispatch({ type: 'SET_DEVICE', value: null });
		dispatch({ type: 'SET_PINPAIRING_DEVICECODE', value: null });
		dispatch({ type: 'SET_PINPAIRING_ACTIVATIONRESPONSE', value: null });

		// Go back to select market
		props.setScreen(ScreenEnum.selectMarket);
	};

	// ----- Countdown -----
	useEffect(() => {
		if (!countdown) return;

		const id = setInterval(() => {
			setCountdown(prev => {
				if (prev > 1) return prev - 1;

				// countdown reached 0, fetch new device code
				(async () => {
					try {
						const dev = await requestDeviceCode(pairingBase);
						console.log('dev:', dev);
						setDeviceData(dev);
						setCountdown(dev.expires_in || 900);
					} catch (err) {
						console.error('Failed to refresh device code:', err);
					}
				})();

				return 0; // temporarily set to 0 until fetch updates it
			});
		}, 1000);

		return () => clearInterval(id);
	}, [countdown]);

	useEffect(() => {
		const resume = state.pairing;
		if (!resume && (!pairingBase || !deliveryBase)) {
			setStatusMsg(t('PairingErrorUrlMissing'));
			return;
		}

		stopRef.current = false;
		deliveryStartedRef.current = false;

		const startFlow = async () => {
			console.log('start flow');
			try {
				setStatusMsg(t('PairingStarted'));
				setErrorMsg('');
				const lsIdToken =
					state.pinpairingActivationResponse?.id_token || '';
				const lsRefresh =
					state.pinpairingActivationResponse?.refresh_token || '';
				if (resume && lsIdToken && lsRefresh) {
					const existingActivation: ExchangeDeviceCodeResponse = {
						id_token: lsIdToken,
						refresh_token: lsRefresh,
						token_type: 'Bearer',
					};
					deliveryStartedRef.current = true;
					setIsDelivering(true);
					setStatusMsg(t('PairingSuccessful'));
					await runDeliveryFlow(existingActivation);
					return;
				}

				// -------- Fresh flow: get device code + poll token ----------
				const dev = await requestDeviceCode(pairingBase);
				const { device_code, interval, expires_in } = dev;
				setDeviceData(dev);
				setCountdown(expires_in || 900);
				let delayMs = Math.max(1000, (interval || 5) * 1000);
				const BASE_DELAY = delayMs;
				const MAX_DELAY = 15000;

				setStatusMsg(t('PairingInstruction'));

				const scheduleTokenPoll = (ms: number) => {
					if (stopRef.current || deliveryStartedRef.current) return;
					tokenPollTimer.set(tokenPoll, ms);
				};

				const tokenPoll = async () => {
					console.log('in token poll');
					if (stopRef.current || deliveryStartedRef.current) return;
					if (countdown === 0) {
						setStatusMsg(t('PairingErrorCodeExpired'));
						return;
					}
					try {
						const formData = new FormData();
						formData.append('device_code', device_code);
						formData.append('client_id', device_code);
						const tokenUrl = `${pairingBase}/oauth/device/token`;
						const response = await fetch(tokenUrl, {
							method: 'POST',
							headers: { 'X-Requested-With': 'XMLHttpRequest' },
							body: formData,
						});
						console.log('responsE:', response);
						const body = await debugLogResponse(
							'device/token',
							response
						);

						if (response.status === 200) {
							console.log('in 200');
							const activationResponse: ExchangeDeviceCodeResponse =
								body;

							// AppState
							dispatch({
								type: 'SET_PINPAIRING_DEVICECODE',
								value: device_code,
							});
							dispatch({
								type: 'SET_PINPAIRING_ACTIVATIONRESPONSE',
								value: activationResponse,
							});
							dispatch({
								type: 'SET_DEVICE',
								value: activationResponse.id_token,
							});
							dispatch({ type: 'SET_PAIRING', value: true });

							deliveryStartedRef.current = true;
							setIsDelivering(true);
							setStatusMsg(t('PairingSuccessful'));
							await runDeliveryFlow(activationResponse);
							return;
						}

						if (
							response.status === 204 ||
							response.status === 403
						) {
							console.log('in 204 or 403');
							delayMs = BASE_DELAY;
							scheduleTokenPoll(delayMs);
							return;
						}

						if (response.status === 429) {
							console.log(' in 429');
							delayMs = getBackoffDelay(
								delayMs,
								MAX_DELAY,
								response.headers.get('Retry-After')
							);
							scheduleTokenPoll(delayMs);
							return;
						}

						scheduleTokenPoll(delayMs);
					} catch {
						scheduleTokenPoll(delayMs);
					}
				};

				tokenPoll();
			} catch (e: any) {
				setErrorMsg(e?.message || 'Error while starting pairing.');
			}
		};

		startFlow();
		return () => {
			stopRef.current = true;
		};
	}, [pairingBase, deliveryBase, dispatch, t]);

	const runDeliveryFlow = async (
		activationResponse: ExchangeDeviceCodeResponse
	) => {
		console.log('in run delivery flow');
		let access = deliveryAccessToken;

		if (!access) {
			access = await fetchDeliveryAccessToken(
				deliveryBase,
				activationResponse.id_token,
				activationResponse.refresh_token,
				stopRef,
				status => {
					if (status === 404)
						setStatusMsg(t('PairingErrorMissingDeliveryToken'));
					if (status === 429) setStatusMsg(t('PairingRateLimited'));
				}
			);
			console.log('access:', access);
			if (!access || stopRef.current) {
				setIsDelivering(false);
				setStatusMsg(t('PairingErrorDeliveryAuthentication'));
				return;
			}

			setDeliveryAccessToken(access);
		}

		const baseContentsUrl = `${deliveryBase}/api/v2/contents?fields-preset=full`;

		const poll = createPollingLoop();

		setIsDelivering(true);
		setStatusMsg(t('PairingSuccessful'));

		await poll({
			url: baseContentsUrl,
			accessToken: access,

			stopRef,

			maxDelay: 15000,
			timerSet: deliveryTimer.set,

			getDelay: getBackoffDelay,

			shouldRetry: (status: number) =>
				status === 204 || status === 404 || status === 429,

			onSuccess: async () => {
				setStatusMsg(t('PairingPricesheetLoaded'));
				setIsDelivering(true);

				navTimer.set(() => {
					setIsDelivering(false);
					props.setScreen(ScreenEnum.selectDevice);
				}, 5000);
			},

			on401: async () => {
				const { accessToken } = await refreshAccessToken(
					deliveryBase,
					activationResponse.id_token,
					activationResponse.refresh_token
				);

				if (accessToken) {
					access = accessToken;
					setDeliveryAccessToken(accessToken);
					return accessToken;
				}

				setIsDelivering(false);
				setStatusMsg(t('PairingErrorTokenRenewal'));
				return null;
			},
		});
	};

	const formatCountdown = (sec: number): string => {
		const date = new Date(sec * 1000);
		const minutes = String(date.getMinutes()).padStart(2, '0');
		const seconds = String(date.getSeconds()).padStart(2, '0');
		return `${minutes}:${seconds}`;
	};

	const userCode = deviceData?.user_code || '';

	return (
		<>
			<div className='top'>
				<div className='pair-device'>
					{props.backBtn(ScreenEnum.selectMarket)}
					<h1>{t('PairDeviceHeadline')}</h1>
					<p className='p1'>{t('PairDeviceText')}</p>
					<Progress
						screen={ScreenEnum.pairDevice}
						setScreen={props.setScreen}
					/>
				</div>

				<div className='pair-device-content'>
					<div className='pin-pairing-pin'>
						{(userCode || '…').split('').map((char, index) => (
							<div className='pin-pairing-box' key={index}>
								<p>{char}</p>
							</div>
						))}
					</div>

					<p>
						{t('PinPairingRefresh', {
							countdown: formatCountdown(countdown),
						})}
					</p>

					{statusMsg && (
						<p className='p1' aria-live='polite'>
							{statusMsg}
						</p>
					)}
					{errorMsg && <p className='p1 error-message'>{errorMsg}</p>}
				</div>
			</div>

			<div
				className={`delivery-overlay ${isDelivering ? 'show' : ''}`}
				aria-live='assertive'
				role='status'
			>
				<div className='content'>
					<p>{statusMsg}</p>
					<div className='spinner' />
				</div>
				<button className='button' onClick={handleCancel}>
					{t('ButtonCancel')}
				</button>
			</div>

			<div className='buttons'>{props.cancelBtn}</div>
		</>
	);
}
